import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BASE_URL } from "../../Constants";
import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";

// ─── Token Helper ─────────────────────────────────────────────────────────────
// Decodes the JWT stored in localStorage and returns { userId, role }.
// Falls back to direct localStorage keys if no token is present.
const getUserFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No token");
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      userId: payload.userId || payload.id || payload.sub || "",
      role:   payload.accountCreatedBy || payload.role || "",
    };
  } catch {
    // Fallback to plain localStorage values
    return {
      userId: localStorage.getItem("userId") || "",
      role:   localStorage.getItem("accountCreatedBy") || "",
    };
  }
};

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconSearch = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconLayers = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
  </svg>
);
const IconUpload = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const IconFilter = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const IconFile = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const IconDatabase = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);
const IconChevronLeft = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconChevronRight = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IconDownload = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// ─── Reusable themed input ────────────────────────────────────────────────────
const FormInput = ({ label, icon: Icon, required, note, className = "", ...props }) => (
  <div className="mb-4">
    <label className="block text-[11px] font-bold text-[#0C4550]/70 uppercase tracking-wider mb-1.5 font-manrope">
      {label} {required && <span className="text-[#F46653] ml-0.5">*</span>}
    </label>
    <div className="relative group">
      <input
        {...props}
        className={`w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-[#0C4550] placeholder-slate-400 focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] outline-none transition-all duration-300 hover:border-[#0C4550]/50 shadow-sm ${className}`}
      />
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#0C4550] transition-colors">
        {Icon && <Icon size={16} />}
      </div>
    </div>
    {note && <p className="mt-1 text-[11px] text-[#0C4550]/60 font-manrope">{note}</p>}
  </div>
);

// ─── Pagination helper ────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
  if (totalPages <= 1) return null;
  const from = (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalItems);
  return (
    <div className="px-6 py-3 border-t border-[#F0EFEB] bg-[#F8F9FA] flex items-center justify-between rounded-b-xl">
      <p className="text-xs font-semibold text-[#64748B] font-manrope">
        Showing <span className="text-[#0C4550]">{from}</span> to <span className="text-[#0C4550]">{to}</span> of <span className="text-[#0C4550]">{totalItems}</span> entries
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-[#E2E8F0] text-[#475569] text-xs font-bold font-manrope hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <IconChevronLeft /> Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-[#E2E8F0] text-[#475569] text-xs font-bold font-manrope hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next <IconChevronRight />
        </button>
      </div>
    </div>
  );
};

// ─── Table for Bulk / File Upload results ────────────────────────────────────
const UploadedFilesTable = ({ files, label }) => {
  const [page, setPage] = useState(1);
  const perPage = 5;
  const totalPages = Math.ceil(files.length / perPage);
  const slice = files.slice((page - 1) * perPage, page * perPage);

  if (!files.length) return null;

  return (
    <div className="bg-white rounded-xl border border-white shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] overflow-hidden">
      <div className="px-6 py-4 bg-white border-b border-[#F0EFEB] flex items-center justify-between">
        <h3 className="font-extrabold text-[#0C4550] font-manrope text-base">Uploaded Files</h3>
        <span className="px-3 py-1 bg-[#F9FAFB] text-[#0C4550]/70 rounded-lg text-[10px] font-extrabold uppercase tracking-widest font-manrope border border-[#E2E8F0]">
          {files.length} Records
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[600px]">
          <thead>
            <tr className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
              <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope w-16">#</th>
              <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope">Database Name</th>
              <th className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope">Download Link</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((file, i) => (
              <tr key={i} className="border-b border-[#F1F5F9] bg-white hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">
                  {(page - 1) * perPage + i + 1}
                </td>
                <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">
                  {file.fileName}
                </td>
                <td className="px-6 py-4">
                  {file.fileUrl ? (
                    <a
                      href={file.fileUrl}
                      download
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#0C4550] text-[13px] font-semibold font-manrope hover:underline"
                    >
                      <IconDownload size={13} /> Download
                    </a>
                  ) : (
                    <span className="text-[13px] text-slate-400 font-manrope">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={files.length}
        itemsPerPage={perPage}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Single Search
// ═══════════════════════════════════════════════════════════════════════════════
const SingleSearchTab = () => {
  const [searchInput, setSearchInput] = useState("");
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiKey = "U3Z9wKe6T2m3NcPYfE45AqLmRz0uKxVh";
  const apiSecret = "nR7Jv6BcX9aUq4pLkWz2HbVmEy31ZgOtMf5QbKdRiYXpUwNA";

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (/^\d*$/.test(val) && val.length <= 10) setSearchInput(val);
  };

  const handleSearch = async () => {
    if (!searchInput || searchInput.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    const timestamp = Date.now().toString();
    const nonce = uuidv4();
    const dataToSign = `${apiKey}|${searchInput}|${timestamp}`;
    const signature = CryptoJS.HmacSHA256(dataToSign, apiSecret).toString(CryptoJS.enc.Base64);

    try {
      const response = await axios.get(`${BASE_URL}/api/batch/single/${searchInput}`, {
        headers: {
          "X-API-KEY": apiKey,
          "X-TIMESTAMP": timestamp,
          "X-NONCE": nonce,
          "X-SIGNATURE": signature,
        },
      });
      const responseData = response.data.data;
      if (responseData?.length > 0) {
        setTableData(responseData);
        toast.success("Data fetched successfully");
      } else {
        setTableData(null);
        toast.error("No data found");
      }
    } catch {
      setTableData(null);
      toast.error("Error fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* Form panel */}
      <div className="lg:col-span-4">
        <div className="bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] border border-white overflow-hidden">
          <div className="px-5 py-4 bg-[#0C4550]/5 border-b border-[#F0EFEB] flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0C4550] flex items-center justify-center text-white shadow-lg shadow-[#0C4550]/20 shrink-0">
              <IconSearch size={15} />
            </div>
            <div>
              <p className="font-bold text-[#0C4550] font-manrope text-sm">Single Lookup</p>
              <p className="text-[#0C4550]/60 text-[10px] font-medium font-manrope leading-tight mt-0.5">
                Enter a 10-digit mobile number to query DND status.
              </p>
            </div>
          </div>
          <div className="p-5">
            <FormInput
              label="Mobile Number"
              icon={IconSearch}
              type="text"
              inputMode="numeric"
              placeholder="Enter 10-digit number"
              value={searchInput}
              onChange={handleInputChange}
              maxLength={10}
              required
            />
            <button
              onClick={handleSearch}
              disabled={loading || searchInput.length < 10}
              className="w-full px-6 py-3 bg-[#0C4550] text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-[#08313a] shadow-lg shadow-[#0C4550]/20 font-manrope text-sm"
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <><IconSearch size={15} /> Search</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results panel */}
      <div className="lg:col-span-8">
        <div className="bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] border border-white overflow-hidden h-full">
          <div className="px-6 py-4 bg-white border-b border-[#F0EFEB] flex items-center justify-between">
            <h3 className="font-extrabold text-[#0C4550] font-manrope text-base">Search Results</h3>
            {tableData && (
              <span className="px-3 py-1 bg-[#C8E6C9] text-[#2F3640] rounded-lg text-[10px] font-extrabold uppercase tracking-widest font-manrope">
                {tableData.length} found
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            {tableData ? (
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                    {["Service Area Code", "Phone Number", "Preferences", "OPS Type", "Phone Type"].map((h) => (
                      <th key={h} className="px-6 py-4 text-[13px] font-bold text-[#475569] font-manrope whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, idx) => (
                    <tr key={idx} className="border-b border-[#F1F5F9] bg-white hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{item.serviceAreaCode}</td>
                      <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{item.phoneNumbers}</td>
                      <td className="px-6 py-4">
                        <span className="bg-[#E1BEE7] text-[#4A148C] px-2.5 py-1 rounded text-[11px] font-extrabold uppercase tracking-wide font-manrope">
                          {item.preferences}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{item.opstype}</td>
                      <td className="px-6 py-4 text-[13px] font-medium text-[#334155] font-manrope">{item.phoneType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-10 text-center text-[#475569] text-sm font-medium font-manrope bg-[#F8F9FA]">
                {loading ? "Fetching data..." : "Enter a mobile number and click Search to see results."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Bulk Search
// ═══════════════════════════════════════════════════════════════════════════════
const BulkSearchTab = () => {
  const [databasename, setDatabasename] = useState("");
  const [numbers, setNumbers] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { userId, role: userRole } = getUserFromToken();

  const fetchData = async () => {
    try {
      const { userId: uid, role } = getUserFromToken();
      const url =
        role === "SUPER_SUPER_ADMIN"
          ? `${BASE_URL}/api/batch/bulk/list`
          : `${BASE_URL}/api/batch/bulk/file/${uid}`;
      const res = await fetch(url);
      const data = await res.json();
      setUploadedFiles(data);
    } catch (err) {
      console.error("Error fetching bulk files:", err);
    }
  };

  useEffect(() => { fetchData(); }, [userId]);

  const handleNumberChange = (e) => {
    const input = e.target.value;
    const arr = input.split(/[\s,]+/);
    if (arr.length > 10000) {
      toast.error("You can only enter up to 10,000 numbers.");
      return;
    }
    setNumbers(input);
  };

  const handleUpload = async () => {
    if (!databasename.trim()) {
      toast.error("Please enter a Database Name");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/api/batch/bulk/search`,
        { fileName: databasename, phoneNumbers: numbers.split(/[\s,]+/).filter(Boolean), userId },
        { headers: { "Content-Type": "application/json" } }
      );
      toast.success("Data uploaded successfully");
      setDatabasename("");
      setNumbers("");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* Form */}
      <div className="lg:col-span-4">
        <div className="bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] border border-white overflow-hidden">
          <div className="px-5 py-4 bg-[#0C4550]/5 border-b border-[#F0EFEB] flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0C4550] flex items-center justify-center text-white shadow-lg shadow-[#0C4550]/20 shrink-0">
              <IconLayers size={15} />
            </div>
            <div>
              <p className="font-bold text-[#0C4550] font-manrope text-sm">Bulk Upload</p>
              <p className="text-[#0C4550]/60 text-[10px] font-medium font-manrope leading-tight mt-0.5">
                Paste up to 10,000 mobile numbers separated by comma or newline.
              </p>
            </div>
          </div>
          <div className="p-5">
            <FormInput
              label="Database Name"
              icon={IconDatabase}
              type="text"
              placeholder="e.g., Campaign_May_2025"
              value={databasename}
              onChange={(e) => setDatabasename(e.target.value)}
              required
            />
            <div className="mb-4">
              <label className="block text-[11px] font-bold text-[#0C4550]/70 uppercase tracking-wider mb-1.5 font-manrope">
                Mobile Numbers <span className="text-[#F46653] ml-0.5">*</span>
              </label>
              <div className="relative group">
                <textarea
                  rows={6}
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-[#0C4550] placeholder-slate-400 text-sm font-medium focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] outline-none transition-all duration-300 hover:border-[#0C4550]/50 shadow-sm resize-none"
                  placeholder="9876543210, 9876543211&#10;9876543212..."
                  value={numbers}
                  onChange={handleNumberChange}
                />
                <div className="absolute top-3 left-3 pointer-events-none text-slate-400 group-focus-within:text-[#0C4550] transition-colors">
                  <IconLayers size={15} />
                </div>
              </div>
              <p className="mt-1 text-[11px] text-[#0C4550]/60 font-manrope">Max 10,000 numbers per upload.</p>
            </div>
            <button
              onClick={handleUpload}
              disabled={loading || !databasename.trim() || !numbers.trim()}
              className="w-full px-6 py-3 bg-[#0C4550] text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-[#08313a] shadow-lg shadow-[#0C4550]/20 font-manrope text-sm"
            >
              {loading ? (
                <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
              ) : (
                <><IconLayers size={15} /> Upload Bulk Data</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="lg:col-span-8">
        <UploadedFilesTable files={uploadedFiles} label="Bulk" />
        {!uploadedFiles.length && (
          <div className="bg-white rounded-xl border border-white shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] p-10 text-center text-[#475569] text-sm font-medium font-manrope bg-[#F8F9FA]">
            No bulk uploads found.
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — File Upload
// ═══════════════════════════════════════════════════════════════════════════════
const FileUploadTab = () => {
  const [databasename, setDatabasename] = useState("");
  const [file, setFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const { userId: uid, role } = getUserFromToken();
      const url =
        role === "SUPER_SUPER_ADMIN"
          ? `${BASE_URL}/api/batch/file/list`
          : `${BASE_URL}/api/batch/files/${uid}`;
      const res = await fetch(url);
      const data = await res.json();
      setUploadedFiles(data);
    } catch (err) {
      console.error("Error fetching file uploads:", err);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 10000);
    return () => clearInterval(id);
  }, [refreshKey]);

  const handleUpload = async () => {
    if (!databasename.trim()) {
      toast.error("Please enter a Database Name");
      return;
    }
    const { userId: uid } = getUserFromToken();
    if (!uid) { toast.error("User ID missing. Please log in again."); return; }

    setLoading(true);
    setDisabled(true);
    const formData = new FormData();
    formData.append("fileName", databasename);
    formData.append("userId", uid);
    if (file) formData.append("file", file);

    try {
      await axios.post(`${BASE_URL}/api/batch/filter`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded successfully");
      setDatabasename("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setRefreshKey((k) => k + 1);
    } catch {
      toast.error("Failed to upload file");
    } finally {
      setLoading(false);
      setTimeout(() => setDisabled(false), 10000);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* Form */}
      <div className="lg:col-span-4">
        <div className="bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] border border-white overflow-hidden">
          <div className="px-5 py-4 bg-[#0C4550]/5 border-b border-[#F0EFEB] flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0C4550] flex items-center justify-center text-white shadow-lg shadow-[#0C4550]/20 shrink-0">
              <IconUpload size={15} />
            </div>
            <div>
              <p className="font-bold text-[#0C4550] font-manrope text-sm">File Upload</p>
              <p className="text-[#0C4550]/60 text-[10px] font-medium font-manrope leading-tight mt-0.5">
                Upload a CSV or Excel file with phone numbers for DND filtering.
              </p>
            </div>
          </div>
          <div className="p-5">
            <FormInput
              label="Database Name"
              icon={IconDatabase}
              type="text"
              placeholder="e.g., DND_Batch_June"
              value={databasename}
              onChange={(e) => setDatabasename(e.target.value)}
              required
            />
            <div className="mb-4">
              <label className="block text-[11px] font-bold text-[#0C4550]/70 uppercase tracking-wider mb-1.5 font-manrope">
                Target File <span className="text-[#F46653] ml-0.5">*</span>
              </label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => setFile(e.target.files[0] || null)}
                accept=".csv,.xls,.xlsx,.txt"
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                disabled={disabled}
                className="w-full px-4 py-2.5 bg-[#F9FAFB] border border-slate-200 text-[#0C4550] text-sm font-semibold rounded-xl hover:border-[#0C4550]/50 transition-all duration-300 flex items-center justify-between"
              >
                <span className="truncate max-w-[80%] text-left">
                  {file ? file.name : "Choose .csv / .xls / .xlsx"}
                </span>
                <IconFile size={16} />
              </button>
              <p className="mt-1 text-[11px] text-[#0C4550]/60 font-manrope">Button re-enables after 10 seconds of upload.</p>
            </div>
            <button
              onClick={handleUpload}
              disabled={loading || disabled || !databasename.trim()}
              className="w-full px-6 py-3 bg-[#0C4550] text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-[#08313a] shadow-lg shadow-[#0C4550]/20 font-manrope text-sm"
            >
              {loading ? (
                <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading...</>
              ) : (
                <><IconUpload size={15} /> Upload File</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="lg:col-span-8">
        <UploadedFilesTable files={uploadedFiles} label="File" />
        {!uploadedFiles.length && (
          <div className="bg-white rounded-xl border border-white shadow-[0_4px_20px_-4px_rgba(12,69,80,0.05)] p-10 text-center text-[#475569] text-sm font-medium font-manrope bg-[#F8F9FA]">
            No file uploads found.
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT — Dndfilter
// ═══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: "single", label: "Single", icon: IconSearch },
  { id: "bulk",   label: "Bulk",   icon: IconLayers },
  { id: "file",   label: "File Upload", icon: IconUpload },
];

const Dndfilter = () => {
  const [activeTab, setActiveTab] = useState("single");

  return (
    <div className="min-h-screen bg-[#F0EFEB] p-4 font-sans flex justify-center text-[#0C4550]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
        .font-manrope { font-family: 'Manrope', sans-serif; }
      `}</style>

      <div className="w-full max-w-6xl space-y-5">

        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/40 backdrop-blur-md px-5 py-4 rounded-2xl border border-white/50 shadow-sm">
          <div className="flex items-center gap-3 pl-1">
            <div className="w-9 h-9 rounded-xl bg-[#0C4550] flex items-center justify-center text-white shadow-lg shadow-[#0C4550]/20 shrink-0">
              <IconFilter size={18} />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-[#0C4550] font-manrope">DND Filter</h1>
              <p className="text-[#0C4550]/60 text-xs font-medium mt-0.5 font-manrope">
                Search and filter phone numbers against the DND registry
              </p>
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex gap-2 bg-white/60 p-1.5 rounded-xl border border-white shadow-sm w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold font-manrope transition-all duration-300 ${
                activeTab === id
                  ? "bg-[#0C4550] text-white shadow-md"
                  : "text-[#0C4550] hover:bg-[#0C4550]/10"
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        {activeTab === "single" && <SingleSearchTab />}
        {activeTab === "bulk"   && <BulkSearchTab />}
        {activeTab === "file"   && <FileUploadTab />}

      </div>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Dndfilter;